import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { documentsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Search, 
  Upload,
  Download,
  Trash2,
  Eye,
  FileText,
  File,
  FileImage,
  FileSpreadsheet,
  Folder,
  UserCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsPage = () => {
  const { t, language } = useLanguage();
  const { canManageDocuments, token, isAdmin, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [clientUploads, setClientUploads] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [caseFilter, setCaseFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('case-docs');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);
  
  const [uploadData, setUploadData] = useState({
    case_id: '',
    description: '',
    file: null
  });

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, casesRes] = await Promise.all([
        documentsAPI.getAll(),
        casesAPI.getAll()
      ]);
      
      // Filter cases and documents for lawyers
      let filteredCases = casesRes.data;
      let filteredDocs = docsRes.data;
      
      if (!isAdmin && user?.role === 'lawyer') {
        // Lawyer sees only their assigned cases
        filteredCases = casesRes.data.filter(c => c.assigned_lawyer_id === user.id);
        const lawyerCaseIds = filteredCases.map(c => c.id);
        // Filter documents to only those belonging to lawyer's cases OR uploaded by this lawyer
        filteredDocs = docsRes.data.filter(d => 
          lawyerCaseIds.includes(d.case_id) || d.uploaded_by === user.id
        );
      }
      
      setDocuments(filteredDocs);
      setCases(filteredCases);
      
      // Fetch client uploads and lawyer uploads (without case) for admin
      if (isAdmin) {
        try {
          const clientUploadsRes = await api.get('/admin/client-uploads');
          // Also get lawyer uploads without case
          const staffUploads = docsRes.data.filter(d => !d.case_id && d.uploader_role === 'lawyer');
          setClientUploads([...clientUploadsRes.data, ...staffUploads]);
        } catch (e) {
          // Just get lawyer uploads if client uploads fails
          const staffUploads = docsRes.data.filter(d => !d.case_id && d.uploader_role === 'lawyer');
          setClientUploads(staffUploads);
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const assignToCase = async (docId, caseId) => {
    try {
      await api.put(`/admin/client-uploads/${docId}/assign?case_id=${caseId}`);
      toast.success(language === 'ar' ? 'تم ربط الملف بالقضية' : 'Document assigned to case');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error(language === 'ar' ? 'يرجى اختيار ملف' : 'Please select a file');
      return;
    }

    // If no case selected, upload as general document (for lawyers to send to admin)
    const caseId = uploadData.case_id || null;

    try {
      await documentsAPI.upload(caseId, uploadData.file, uploadData.description);
      toast.success(language === 'ar' ? 'تم رفع المستند' : 'Document uploaded');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await documentsAPI.delete(id);
      toast.success(language === 'ar' ? 'تم حذف المستند' : 'Document deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error'));
    }
  };

  const handleDownload = (doc) => {
    const url = documentsAPI.download(doc.id);
    window.open(`${url}?token=${token}`, '_blank');
  };

  const resetForm = () => {
    setUploadData({
      case_id: '',
      description: '',
      file: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <FileImage className="w-5 h-5" strokeWidth={1.5} />;
    if (fileType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" strokeWidth={1.5} />;
    if (fileType?.includes('sheet') || fileType?.includes('excel')) return <FileSpreadsheet className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />;
    return <File className="w-5 h-5" strokeWidth={1.5} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem ? `${caseItem.case_number}` : '-';
  };

  const filteredDocuments = documents.filter(d => {
    const matchesSearch = d.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (d.description && d.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCase = caseFilter === 'all' || d.case_id === caseFilter;
    return matchesSearch && matchesCase;
  });

  // Group documents by case
  const documentsByCase = {};
  filteredDocuments.forEach(doc => {
    if (!documentsByCase[doc.case_id]) {
      documentsByCase[doc.case_id] = [];
    }
    documentsByCase[doc.case_id].push(doc);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="documents-page">
      <Header title={t('documents')} />
      
      <div className="p-6 space-y-6">
        {/* Tabs for Admin */}
        {isAdmin && clientUploads.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="case-docs" className="gap-2">
                <Folder className="w-4 h-4" />
                {language === 'ar' ? 'مستندات القضايا' : 'Case Documents'}
              </TabsTrigger>
              <TabsTrigger value="client-uploads" className="gap-2">
                <UserCircle className="w-4 h-4" />
                {language === 'ar' ? 'ملفات العملاء' : 'Client Uploads'}
                <Badge variant="destructive" className="ml-1">{clientUploads.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="client-uploads" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-[hsl(var(--accent))]" />
                    {language === 'ar' ? 'ملفات من العملاء والموظفين' : 'Files from Clients & Staff'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {clientUploads.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border bg-card">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-sm bg-muted">
                            {getFileIcon(doc.file_type)}
                          </div>
                          <div>
                            <p className="font-medium">{doc.filename}</p>
                            <p className="text-sm text-muted-foreground">
                              {language === 'ar' ? 'من: ' : 'From: '}
                              {doc.client_name || doc.uploader_name || 'Unknown'} 
                              {doc.uploader_role === 'lawyer' && (
                                <Badge variant="outline" className="ml-2">{language === 'ar' ? 'محامي' : 'Lawyer'}</Badge>
                              )}
                              {' • '}{formatFileSize(doc.file_size)}
                            </p>
                            {doc.description && (
                              <p className="text-sm text-muted-foreground">{doc.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select onValueChange={(caseId) => assignToCase(doc.id, caseId)}>
                            <SelectTrigger className="w-[180px]">
                              <SelectValue placeholder={language === 'ar' ? 'ربط بقضية' : 'Assign to case'} />
                            </SelectTrigger>
                            <SelectContent>
                              {cases.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.case_number} - {c.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="case-docs" className="mt-4">
              {renderCaseDocuments()}
            </TabsContent>
          </Tabs>
        )}

        {/* Regular view if no client uploads or not admin */}
        {(!isAdmin || clientUploads.length === 0) && renderCaseDocuments()}
        
        {/* Preview Dialog - Enhanced for PDF, Images, and other files */}
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-5xl h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {getFileIcon(previewDoc?.file_type)}
                {previewDoc?.filename}
              </DialogTitle>
            </DialogHeader>
            {previewDoc && (
              <div className="flex-1 h-full overflow-hidden">
                {previewDoc.file_type?.includes('pdf') ? (
                  <iframe
                    src={`${documentsAPI.download(previewDoc.id)}?token=${token}`}
                    className="w-full h-[calc(85vh-100px)] rounded-sm border"
                    title={previewDoc.filename}
                  />
                ) : previewDoc.file_type?.includes('image') ? (
                  <div className="flex items-center justify-center h-[calc(85vh-100px)] bg-muted/20 rounded-sm">
                    <img
                      src={`${documentsAPI.download(previewDoc.id)}?token=${token}`}
                      alt={previewDoc.filename}
                      className="max-w-full max-h-full object-contain rounded-sm"
                    />
                  </div>
                ) : previewDoc.file_type?.includes('text') || 
                   previewDoc.filename?.endsWith('.txt') ||
                   previewDoc.filename?.endsWith('.json') ||
                   previewDoc.filename?.endsWith('.md') ? (
                  <iframe
                    src={`${documentsAPI.download(previewDoc.id)}?token=${token}`}
                    className="w-full h-[calc(85vh-100px)] rounded-sm border bg-white"
                    title={previewDoc.filename}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-[calc(85vh-100px)] bg-muted/20 rounded-sm">
                    <File className="w-20 h-20 text-muted-foreground mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground mb-2">
                      {language === 'ar' ? 'لا يمكن معاينة هذا النوع من الملفات' : 'This file type cannot be previewed'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">{previewDoc.file_type}</p>
                    <Button onClick={() => handleDownload(previewDoc)} className="gap-2">
                      <Download className="w-4 h-4" />
                      {language === 'ar' ? 'تحميل الملف' : 'Download File'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  function renderCaseDocuments() {
    return (
      <>
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-documents"
              />
            </div>
            <Select value={caseFilter} onValueChange={setCaseFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-case">
                <Folder className="w-4 h-4 mr-2" />
                <SelectValue placeholder={language === 'ar' ? 'كل القضايا' : 'All Cases'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'ar' ? 'كل القضايا' : 'All Cases'}</SelectItem>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.case_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canManageDocuments && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="upload-document-btn">
                  <Upload className="w-4 h-4" />
                  {t('uploadDocument')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('uploadDocument')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{language === 'ar' ? 'القضية' : 'Case'} ({language === 'ar' ? 'اختياري' : 'Optional'})</Label>
                    <Select 
                      value={uploadData.case_id || "none"} 
                      onValueChange={(v) => setUploadData({...uploadData, case_id: v === "none" ? "" : v})}
                    >
                      <SelectTrigger data-testid="upload-case-select">
                        <SelectValue placeholder={language === 'ar' ? 'اختر قضية أو اتركه فارغ' : 'Select case or leave empty'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          {language === 'ar' ? '📤 إرسال للإدارة (بدون قضية)' : '📤 Send to Admin (no case)'}
                        </SelectItem>
                        {cases.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.case_number} - {c.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!uploadData.case_id && (
                      <p className="text-sm text-muted-foreground">
                        {language === 'ar' ? 'سيتم إرسال الملف للإدارة للمراجعة' : 'File will be sent to admin for review'}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'ar' ? 'الملف' : 'File'} *</Label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                      className="cursor-pointer"
                      data-testid="upload-file-input"
                    />
                    {uploadData.file && (
                      <p className="text-sm text-muted-foreground">
                        {uploadData.file.name} ({formatFileSize(uploadData.file.size)})
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>{t('description')}</Label>
                    <Textarea
                      value={uploadData.description}
                      onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                      rows={2}
                      data-testid="upload-description-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="btn-accent" data-testid="upload-submit-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      {t('uploadDocument')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Documents Grid */}
        {Object.keys(documentsByCase).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(documentsByCase).map(([caseId, docs]) => {
              const caseInfo = cases.find(c => c.id === caseId);
              return (
                <Card key={caseId} data-testid={`case-documents-${caseId}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Folder className="w-5 h-5 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      {caseInfo ? `${caseInfo.case_number} - ${caseInfo.title}` : caseId}
                      <span className="text-sm text-muted-foreground font-normal ml-2">
                        ({docs.length} {language === 'ar' ? 'ملف' : 'files'})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {docs.map(doc => (
                        <div 
                          key={doc.id}
                          className="flex items-start gap-3 p-3 rounded-sm border border-border hover:bg-muted/50 transition-colors group"
                        >
                          <div className="p-2 rounded-sm bg-muted">
                            {getFileIcon(doc.file_type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate" title={doc.filename}>
                              {doc.filename}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(doc.file_size)}
                            </p>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground truncate mt-1" title={doc.description}>
                                {doc.description}
                              </p>
                            )}
                            <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleDownload(doc)}
                                title={t('download')}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => setPreviewDoc(doc)}
                                title={t('preview')}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              {canManageDocuments && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => handleDelete(doc.id)}
                                  title={t('delete')}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">{t('noData')}</p>
            {cases.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'ar' ? 'يرجى إنشاء قضية أولاً لرفع المستندات' : 'Please create a case first to upload documents'}
              </p>
            )}
          </Card>
        )}
      </>
    );
  }
};

export default DocumentsPage;
